import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { Border, Color, Grid, Shadow, Transition, Type } from '../app/StyleGuide';

export type SelectOption = {
  value: string | number;
  label: string | React.ReactNode;
  disabled?: boolean;
};

export type SelectProps = {
  options: SelectOption[];
  value?: string | number;
  onChange: (value: string | number) => void;
  placeholder?: string;
  disabled?: boolean;
  name?: string;
  id?: string;
  className?: string;
  maxHeight?: string;
  width?: string;
  error?: boolean;
  errorMessage?: string;
};

const SelectContainer = styled.div<{ width?: string; disabled?: boolean }>`
  position: relative;
  width: ${props => props.width || '100%'};
  opacity: ${props => (props.disabled ? 0.6 : 1)};
  pointer-events: ${props => (props.disabled ? 'none' : 'auto')};
`;

const SelectButton = styled.button<{ isOpen: boolean; error?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  box-sizing: border-box;
  height: 42px;
  padding: 8px 16px;
  background: ${Color.white};
  border: 1px solid ${props => (props.error ? Color.Red._50 : props.isOpen ? Color.Blue._50 : Color.Blue._20)};
  border-radius: ${Border.radius};
  font-size: ${Type.Scale._2};
  line-height: ${Type.Leading.taller};
  color: ${Type.Color.dark};
  cursor: pointer;
  box-shadow: inset ${Shadow.small};
  transition: ${Transition.fast};
  text-align: left;
  outline: none;

  &:hover {
    border-color: ${props => (props.error ? Color.Red._50 : Color.Blue._50)};
  }

  &:focus {
    border-color: ${props => (props.error ? Color.Red._50 : Color.Blue._50)};
    box-shadow: ${props =>
      props.error
        ? `inset 0px 0px 0px rgba(0, 0, 0, 0), 0px 0px 0px 1px ${Color.Red._50}, 0px 0px 0px 5px rgba(179, 76, 76, 0.3)`
        : `inset 0px 0px 0px rgba(0, 0, 0, 0), 0px 0px 0px 1px ${Color.Blue._50}, 0px 0px 0px 5px rgba(172, 211, 239, 0.3)`};
  }
`;

const SelectValue = styled.span<{ hasValue: boolean }>`
  color: ${props => (props.hasValue ? '#495057' : '#6c757d')};
  font-weight: ${props => (props.hasValue ? '500' : '400')};
`;

const SelectArrow = styled.span<{ isOpen: boolean }>`
  display: inline-block;
  width: 0;
  height: 0;
  border-left: 5px solid transparent;
  border-right: 5px solid transparent;
  border-top: 5px solid #6c757d;
  transition: transform 0.2s ease;
  transform: ${props => (props.isOpen ? 'rotate(180deg)' : 'rotate(0deg)')};
  margin-left: 8px;
`;

const Dropdown = styled.div<{ isOpen: boolean; maxHeight?: string }>`
  position: fixed;
  top: auto;
  left: auto;
  right: auto;
  background: #ffffff;
  border: 2px solid #e9ecef;
  border-radius: 6px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  z-index: 2147483005; // above Intercom and Modal
  opacity: ${props => (props.isOpen ? 1 : 0)};
  visibility: ${props => (props.isOpen ? 'visible' : 'hidden')};
  transform: ${props => (props.isOpen ? 'translateY(0)' : 'translateY(-10px)')};
  transition: all 0.2s ease;
  max-height: ${props => props.maxHeight || '300px'};
  overflow-y: auto;
  width: auto;
`;

const OptionList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
`;

const OptionItem = styled.li<{ isSelected: boolean; isDisabled: boolean }>`
  padding: 12px 16px;
  cursor: ${props => (props.isDisabled ? 'not-allowed' : 'pointer')};
  background: ${props => (props.isSelected ? '#e3f2fd' : 'transparent')};
  color: ${props => (props.isDisabled ? '#adb5bd' : props.isSelected ? '#1976d2' : '#495057')};
  font-weight: ${props => (props.isSelected ? '600' : '400')};
  transition: background-color 0.15s ease;

  &:hover {
    background: ${props => (props.isDisabled ? 'transparent' : props.isSelected ? '#e3f2fd' : '#f8f9fa')};
  }

  &:first-child {
    border-radius: 0;
  }

  &:last-child {
    border-radius: 0 0 4px 4px;
  }
`;

const ErrorMessage = styled.div`
  color: ${Color.Red._70};
  font-size: ${Type.Scale._2};
  margin-top: ${Grid._2};
  margin-left: ${Grid._2};
`;

const HiddenInput = styled.input`
  position: absolute;
  left: -9999px;
  opacity: 0;
  pointer-events: none;
`;

export const Select: React.FC<SelectProps> = ({
  options,
  value,
  onChange,
  placeholder = 'Select an option...',
  disabled = false,
  name,
  id,
  className,
  maxHeight = '300px',
  width,
  error = false,
  errorMessage,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState<SelectOption | null>(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (value !== undefined) {
      const option = options.find(opt => opt.value === value);
      setSelectedOption(option || null);
    } else {
      setSelectedOption(null);
    }
  }, [value, options]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const targetNode = event.target as Node;
      const clickedInsideContainer = containerRef.current?.contains(targetNode);
      const clickedInsideDropdown = dropdownRef.current?.contains(targetNode);

      if (!clickedInsideContainer && !clickedInsideDropdown) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  const updateDropdownPosition = useCallback(() => {
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom,
        left: rect.left,
        width: rect.width,
      });
    }
  }, []);

  useEffect(() => {
    const handleResize = () => {
      if (isOpen) {
        updateDropdownPosition();
      }
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [isOpen, updateDropdownPosition]);

  const handleToggle = useCallback(() => {
    if (!disabled) {
      if (!isOpen) {
        updateDropdownPosition();
      }
      setIsOpen(prev => !prev);
    }
  }, [disabled, isOpen, updateDropdownPosition]);

  const handleOptionSelect = useCallback(
    (option: SelectOption) => {
      if (option.disabled) return;

      setSelectedOption(option);
      onChange(option.value);
      setIsOpen(false);
    },
    [onChange]
  );

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (disabled) return;

      switch (event.key) {
        case 'Enter':
        case ' ':
          event.preventDefault();
          handleToggle();
          break;
        case 'ArrowDown':
          event.preventDefault();
          if (!isOpen) {
            setIsOpen(true);
          } else {
            // Focus next option
            const currentIndex = selectedOption ? options.findIndex(opt => opt.value === selectedOption.value) : -1;
            const nextIndex = (currentIndex + 1) % options.length;
            const nextOption = options[nextIndex];
            if (!nextOption.disabled) {
              handleOptionSelect(nextOption);
            }
          }
          break;
        case 'ArrowUp':
          event.preventDefault();
          if (!isOpen) {
            setIsOpen(true);
          } else {
            // Focus previous option
            const currentIndex = selectedOption ? options.findIndex(opt => opt.value === selectedOption.value) : -1;
            const prevIndex = currentIndex <= 0 ? options.length - 1 : currentIndex - 1;
            const prevOption = options[prevIndex];
            if (!prevOption.disabled) {
              handleOptionSelect(prevOption);
            }
          }
          break;
      }
    },
    [disabled, isOpen, selectedOption, options, handleToggle, handleOptionSelect]
  );

  const displayValue = selectedOption ? selectedOption.label : placeholder;

  const renderDropdown = () => {
    if (!isOpen) return null;

    const dropdownContent = (
      <Dropdown
        ref={dropdownRef}
        isOpen={isOpen}
        maxHeight={maxHeight}
        style={{
          top: `${dropdownPosition.top}px`,
          left: `${dropdownPosition.left}px`,
          width: `${dropdownPosition.width}px`,
        }}
        role="listbox"
        aria-labelledby={id}
      >
        <OptionList>
          {options.map(option => (
            <OptionItem
              key={option.value}
              isSelected={selectedOption?.value === option.value}
              isDisabled={option.disabled || false}
              onClick={() => handleOptionSelect(option)}
              role="option"
              aria-selected={selectedOption?.value === option.value}
              aria-disabled={option.disabled}
              tabIndex={-1}
            >
              {option.label}
            </OptionItem>
          ))}
        </OptionList>
      </Dropdown>
    );

    return createPortal(dropdownContent, document.body);
  };

  return (
    <SelectContainer ref={containerRef} width={width} disabled={disabled} className={className}>
      <SelectButton
        type="button"
        onClick={handleToggle}
        onKeyDown={handleKeyDown}
        isOpen={isOpen}
        error={error}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-labelledby={id}
        disabled={disabled}
      >
        <SelectValue hasValue={!!selectedOption}>{displayValue}</SelectValue>
        <SelectArrow isOpen={isOpen} />
      </SelectButton>

      {renderDropdown()}

      {/* Hidden input for form compatibility */}
      {name && <HiddenInput type="hidden" name={name} value={value || ''} readOnly />}

      {error && errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}
    </SelectContainer>
  );
};

export default Select;
